import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = useCallback(async () => {
    try {
      const token = authService.getToken();
      if (token) {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        const response = await fetch(`${API_URL}/auth/profile/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const userData = await response.json();
          localStorage.setItem('user', JSON.stringify(userData));
          setUser(userData);
          console.log('Profile refreshed:', userData);
          return userData;
        } else {
          console.error('Failed to fetch profile');
          setUser(null);
          return null;
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Always fetch fresh profile data if token exists
    const token = authService.getToken();
    if (token) {
      console.log('Fetching fresh user profile...');
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [fetchUserProfile]);

  const login = async (credentials, forceLogout = false) => {
    const data = await authService.login(credentials, forceLogout);
    // Fetch fresh profile data to ensure we have enrolled_courses
    await fetchUserProfile();
    return data;
  };

  const register = async (userData) => {
    const data = await authService.register(userData);
    // Fetch fresh profile data to ensure we have enrolled_courses
    await fetchUserProfile();
    return data;
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    refreshProfile: fetchUserProfile,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
